import React, { useState, useEffect } from 'react';
import { CalendarDaysIcon, ClockIcon, ExclamationTriangleIcon, CheckCircleIcon, ArrowDownTrayIcon, TrashIcon } from '@heroicons/react/24/outline';
import apiClient from '../services/api';
import { useTranslation } from 'react-i18next';

interface DeadlineType {
  id: string;
  name: string;
  description: string;
}

interface MatterType {
  id: string;
  description: string;
  days: number;
}

interface CalculatedDeadline {
  deadline_type: string;
  matter_type: string;
  trigger_date: string;
  deadline_date: string;
  days_until_deadline: number;
  days_from_trigger: number;
  description: string;
  jurisdiction: string;
  warning_level: string;
  notes?: string;
}

interface Jurisdiction {
  country: string;
  country_name: string;
  states: Record<string, string>;
}

interface Country {
  code: string;
  name: string;
}

export const DeadlineCalculator: React.FC = () => {
  const { t, i18n } = useTranslation();

  // Form state
  const [deadlineTypes, setDeadlineTypes] = useState<DeadlineType[]>([]);
  const [matterTypes, setMatterTypes] = useState<MatterType[]>([]);
  const [countries, setCountries] = useState<Country[]>([]);
  const [jurisdiction, setJurisdiction] = useState<Jurisdiction | null>(null);

  const [selectedDeadlineType, setSelectedDeadlineType] = useState('');
  const [selectedMatterTypes, setSelectedMatterTypes] = useState<string[]>([]);
  const [selectedCountry, setSelectedCountry] = useState('US');
  const [selectedState, setSelectedState] = useState('federal');
  const [triggerDate, setTriggerDate] = useState('');

  // Results state
  const [calculatedDeadlines, setCalculatedDeadlines] = useState<CalculatedDeadline[]>([]);
  const [isCalculating, setIsCalculating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Loading states
  const [isLoadingTypes, setIsLoadingTypes] = useState(true);
  const [isLoadingMatterTypes, setIsLoadingMatterTypes] = useState(false);

  // Load deadline types and countries on mount
  useEffect(() => {
    loadDeadlineTypes();
    loadCountries();
    loadJurisdictions();
  }, []);

  // Reload jurisdictions when country changes
  useEffect(() => {
    loadJurisdictions();
    setSelectedState('federal'); // Reset to federal when country changes
    setMatterTypes([]); // Clear matter types
    setSelectedMatterTypes([]); // Clear selected matter types
  }, [selectedCountry]);

  // Load matter types when deadline type or jurisdiction changes
  useEffect(() => {
    if (selectedDeadlineType) {
      loadMatterTypes();
    }
  }, [selectedDeadlineType, selectedCountry, selectedState]);

  const loadDeadlineTypes = async () => {
    setIsLoadingTypes(true);
    try {
      const response = await apiClient.get('/deadline-calculator/types', {
        params: { language: i18n.language }
      });
      setDeadlineTypes(response.data.deadline_types);
    } catch (err) {
      console.error('Failed to load deadline types:', err);
      setError(t('deadlineCalculator:errors.loadTypes', 'Failed to load deadline types'));
    } finally {
      setIsLoadingTypes(false);
    }
  };

  const loadCountries = async () => {
    try {
      const response = await apiClient.get('/deadline-calculator/countries', {
        params: { language: i18n.language }
      });
      setCountries(response.data.countries);
    } catch (err) {
      console.error('Failed to load countries:', err);
    }
  };

  const loadJurisdictions = async () => {
    try {
      const response = await apiClient.get('/deadline-calculator/jurisdictions', {
        params: { country: selectedCountry, language: i18n.language }
      });
      setJurisdiction(response.data);
    } catch (err) {
      console.error('Failed to load jurisdictions:', err);
    }
  };

  const loadMatterTypes = async () => {
    setIsLoadingMatterTypes(true);
    try {
      const response = await apiClient.get(`/deadline-calculator/matter-types/${selectedDeadlineType}`, {
        params: { country: selectedCountry, state: selectedState, language: i18n.language }
      });
      setMatterTypes(response.data.matter_types);
      setSelectedMatterTypes([]); // Reset selections when matter types change
    } catch (err) {
      console.error('Failed to load matter types:', err);
      setMatterTypes([]);
    } finally {
      setIsLoadingMatterTypes(false);
    }
  };

  const handleMatterTypeToggle = (matterId: string) => {
    setSelectedMatterTypes(prev => {
      if (prev.includes(matterId)) {
        return prev.filter(id => id !== matterId);
      } else {
        return [...prev, matterId];
      }
    });
  };

  const handleSelectAllMatterTypes = () => {
    if (selectedMatterTypes.length === matterTypes.length) {
      setSelectedMatterTypes([]);
    } else {
      setSelectedMatterTypes(matterTypes.map(m => m.id));
    }
  };

  const handleCalculate = async () => {
    if (!selectedDeadlineType || selectedMatterTypes.length === 0 || !triggerDate) {
      setError(t('deadlineCalculator:errors.missingFields', 'Please fill in all required fields'));
      return;
    }

    setIsCalculating(true);
    setError(null);

    try {
      const response = await apiClient.post('/deadline-calculator/calculate-multiple', {
        deadline_type: selectedDeadlineType,
        matter_types: selectedMatterTypes,
        trigger_date: triggerDate,
        country: selectedCountry,
        state: selectedState,
        language: i18n.language
      });

      setCalculatedDeadlines(response.data.deadlines);
    } catch (err: any) {
      console.error('Failed to calculate deadlines:', err);
      setError(err.response?.data?.detail || t('deadlineCalculator:errors.calculation', 'Failed to calculate deadlines'));
    } finally {
      setIsCalculating(false);
    }
  };

  const handleClearResults = () => {
    setCalculatedDeadlines([]);
  };

  const handleExport = async (format: 'ical' | 'csv') => {
    if (calculatedDeadlines.length === 0) return;

    try {
      const response = await apiClient.get(`/deadline-calculator/export/${format}`, {
        params: {
          deadlines: JSON.stringify(calculatedDeadlines),
          language: i18n.language
        }
      });

      const { content, filename, mime_type } = response.data;

      // Create and trigger download
      const blob = new Blob([content], { type: mime_type });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to export deadlines:', err);
      setError(t('deadlineCalculator:errors.export', 'Failed to export deadlines'));
    }
  };

  const getWarningIcon = (level: string) => {
    switch (level) {
      case 'overdue':
        return <ExclamationTriangleIcon className="h-5 w-5 text-red-600 dark:text-red-400" />;
      case 'urgent':
        return <ExclamationTriangleIcon className="h-5 w-5 text-orange-600 dark:text-orange-400" />;
      case 'caution':
        return <ClockIcon className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />;
      default:
        return <CheckCircleIcon className="h-5 w-5 text-green-600 dark:text-green-400" />;
    }
  };

  const getWarningColor = (level: string) => {
    switch (level) {
      case 'overdue':
        return 'bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-800';
      case 'urgent':
        return 'bg-orange-50 dark:bg-orange-900/30 border-orange-200 dark:border-orange-800';
      case 'caution':
        return 'bg-yellow-50 dark:bg-yellow-900/30 border-yellow-200 dark:border-yellow-800';
      default:
        return 'bg-green-50 dark:bg-green-900/30 border-green-200 dark:border-green-800';
    }
  };

  const getWarningText = (level: string) => {
    switch (level) {
      case 'overdue':
        return t('deadlineCalculator:status.overdue', 'Overdue');
      case 'urgent':
        return t('deadlineCalculator:status.urgent', 'Urgent');
      case 'caution':
        return t('deadlineCalculator:status.caution', 'Caution');
      default:
        return t('deadlineCalculator:status.safe', 'Safe');
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(i18n.language, {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8 text-center">
          <img
            src="/scales-logo.png"
            alt="Scales of Justice"
            className="h-24 w-24 mx-auto mb-4"
          />
          <h1 className="text-4xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            {t('deadlineCalculator:title', 'Legal Deadline Calculator')}
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-400">
            {t('deadlineCalculator:subtitle', 'Calculate important legal deadlines based on case type and jurisdiction')}
          </p>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-800 dark:text-red-300">
            {error}
            <button
              onClick={() => setError(null)}
              className="ml-4 text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200"
            >
              {t('deadlineCalculator:common.dismiss', 'Dismiss')}
            </button>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column - Input Form */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
              {t('deadlineCalculator:form.title', 'Calculate Deadlines')}
            </h2>

            {/* Jurisdiction Selection */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('deadlineCalculator:form.jurisdiction', 'Jurisdiction')}
              </label>
              <div className="grid grid-cols-2 gap-4">
                <select
                  value={selectedCountry}
                  onChange={(e) => setSelectedCountry(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {countries.map((country) => (
                    <option key={country.code} value={country.code}>{country.name}</option>
                  ))}
                </select>
                <select
                  value={selectedState}
                  onChange={(e) => setSelectedState(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {jurisdiction?.states && Object.entries(jurisdiction.states)
                    .sort(([, a], [, b]) => a.localeCompare(b))
                    .map(([code, name]) => (
                      <option key={code} value={code}>{name}</option>
                    ))}
                </select>
              </div>
              {selectedCountry !== 'US' && (
                <div className="mt-2 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                  <p className="text-sm text-amber-800 dark:text-amber-200">
                    {t('deadlineCalculator:form.limitedDataNote', 'Detailed deadline rules for this jurisdiction are still being developed. Federal/default rules will be used where specific rules are not available.')}
                  </p>
                </div>
              )}
            </div>

            {/* Deadline Type Selection */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('deadlineCalculator:form.deadlineType', 'Deadline Type')}
              </label>
              {isLoadingTypes ? (
                <div className="animate-pulse h-10 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
              ) : (
                <select
                  value={selectedDeadlineType}
                  onChange={(e) => setSelectedDeadlineType(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">{t('deadlineCalculator:form.selectDeadlineType', 'Select a deadline type...')}</option>
                  {deadlineTypes.map((type) => (
                    <option key={type.id} value={type.id}>{type.name}</option>
                  ))}
                </select>
              )}
              {selectedDeadlineType && deadlineTypes.find(t => t.id === selectedDeadlineType) && (
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                  {deadlineTypes.find(t => t.id === selectedDeadlineType)?.description}
                </p>
              )}
            </div>

            {/* Trigger Date */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('deadlineCalculator:form.triggerDate', 'Trigger Date')}
              </label>
              <input
                type="date"
                value={triggerDate}
                onChange={(e) => setTriggerDate(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                {t('deadlineCalculator:form.triggerDateHelp', 'The date the event occurred (e.g., contract signing, injury date, service date)')}
              </p>
            </div>

            {/* Matter Type Selection */}
            {selectedDeadlineType && (
              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('deadlineCalculator:form.matterTypes', 'Matter Types')}
                  </label>
                  <button
                    onClick={handleSelectAllMatterTypes}
                    className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
                  >
                    {selectedMatterTypes.length === matterTypes.length
                      ? t('deadlineCalculator:form.deselectAll', 'Deselect All')
                      : t('deadlineCalculator:form.selectAll', 'Select All')}
                  </button>
                </div>
                {isLoadingMatterTypes ? (
                  <div className="space-y-2">
                    {[1, 2, 3].map(i => (
                      <div key={i} className="animate-pulse h-12 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
                    ))}
                  </div>
                ) : matterTypes.length > 0 ? (
                  <div className="max-h-64 overflow-y-auto space-y-2 border border-gray-200 dark:border-gray-600 rounded-lg p-2">
                    {matterTypes.map((matter) => (
                      <label
                        key={matter.id}
                        className={`flex items-start p-3 rounded-lg cursor-pointer transition-colors ${
                          selectedMatterTypes.includes(matter.id)
                            ? 'bg-blue-50 dark:bg-blue-900/30 border border-blue-300 dark:border-blue-700'
                            : 'bg-gray-50 dark:bg-gray-700 border border-transparent hover:bg-gray-100 dark:hover:bg-gray-600'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedMatterTypes.includes(matter.id)}
                          onChange={() => handleMatterTypeToggle(matter.id)}
                          className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600 rounded"
                        />
                        <div className="ml-3 flex-1">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {matter.description}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {matter.days} {t('deadlineCalculator:form.days', 'days')}
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                ) : (
                  <div className="text-sm text-gray-500 dark:text-gray-400 p-4 text-center border border-gray-200 dark:border-gray-600 rounded-lg">
                    {t('deadlineCalculator:form.noMatterTypes', 'No matter types available for this selection')}
                  </div>
                )}
              </div>
            )}

            {/* Calculate Button */}
            <button
              onClick={handleCalculate}
              disabled={isCalculating || !selectedDeadlineType || selectedMatterTypes.length === 0 || !triggerDate}
              className="w-full px-6 py-3 bg-blue-600 dark:bg-blue-700 hover:bg-blue-700 dark:hover:bg-blue-600 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {isCalculating ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  {t('deadlineCalculator:form.calculating', 'Calculating...')}
                </>
              ) : (
                <>
                  <CalendarDaysIcon className="h-5 w-5 mr-2" />
                  {t('deadlineCalculator:form.calculate', 'Calculate Deadlines')}
                </>
              )}
            </button>
          </div>

          {/* Right Column - Results */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                {t('deadlineCalculator:results.title', 'Calculated Deadlines')}
              </h2>
              {calculatedDeadlines.length > 0 && (
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleExport('ical')}
                    className="px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg transition-colors flex items-center"
                    title={t('deadlineCalculator:export.ical', 'Export to Calendar')}
                  >
                    <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
                    iCal
                  </button>
                  <button
                    onClick={() => handleExport('csv')}
                    className="px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg transition-colors flex items-center"
                    title={t('deadlineCalculator:export.csv', 'Export to CSV')}
                  >
                    <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
                    CSV
                  </button>
                  <button
                    onClick={handleClearResults}
                    className="px-3 py-1.5 text-sm bg-red-100 dark:bg-red-900/30 hover:bg-red-200 dark:hover:bg-red-900/50 text-red-700 dark:text-red-300 rounded-lg transition-colors flex items-center"
                    title={t('deadlineCalculator:results.clear', 'Clear Results')}
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>

            {calculatedDeadlines.length > 0 ? (
              <div className="space-y-4">
                {/* Summary */}
                <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg mb-4">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">
                      {t('deadlineCalculator:results.jurisdiction', 'Jurisdiction')}: <span className="font-medium text-gray-900 dark:text-white">{calculatedDeadlines[0]?.jurisdiction}</span>
                    </span>
                    <span className="text-gray-600 dark:text-gray-400">
                      {t('deadlineCalculator:results.triggerDate', 'Trigger Date')}: <span className="font-medium text-gray-900 dark:text-white">{formatDate(calculatedDeadlines[0]?.trigger_date)}</span>
                    </span>
                  </div>
                </div>

                {/* Deadline Cards */}
                {calculatedDeadlines.map((deadline, index) => (
                  <div
                    key={`${deadline.matter_type}-${index}`}
                    className={`p-4 rounded-lg border ${getWarningColor(deadline.warning_level)}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center mb-2">
                          {getWarningIcon(deadline.warning_level)}
                          <span className={`ml-2 text-xs font-medium px-2 py-0.5 rounded ${
                            deadline.warning_level === 'overdue' ? 'bg-red-200 dark:bg-red-800 text-red-800 dark:text-red-200' :
                            deadline.warning_level === 'urgent' ? 'bg-orange-200 dark:bg-orange-800 text-orange-800 dark:text-orange-200' :
                            deadline.warning_level === 'caution' ? 'bg-yellow-200 dark:bg-yellow-800 text-yellow-800 dark:text-yellow-200' :
                            'bg-green-200 dark:bg-green-800 text-green-800 dark:text-green-200'
                          }`}>
                            {getWarningText(deadline.warning_level)}
                          </span>
                        </div>
                        <h3 className="font-medium text-gray-900 dark:text-white mb-1">
                          {deadline.description}
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {deadline.deadline_type}
                        </p>
                      </div>
                      <div className="text-right ml-4">
                        <div className="text-lg font-semibold text-gray-900 dark:text-white">
                          {formatDate(deadline.deadline_date)}
                        </div>
                        <div className={`text-sm font-medium ${
                          deadline.days_until_deadline < 0 ? 'text-red-600 dark:text-red-400' :
                          deadline.days_until_deadline <= 7 ? 'text-orange-600 dark:text-orange-400' :
                          deadline.days_until_deadline <= 30 ? 'text-yellow-600 dark:text-yellow-400' :
                          'text-green-600 dark:text-green-400'
                        }`}>
                          {deadline.days_until_deadline < 0
                            ? t('deadlineCalculator:results.daysOverdue', '{{days}} days overdue', { days: Math.abs(deadline.days_until_deadline) })
                            : deadline.days_until_deadline === 0
                            ? t('deadlineCalculator:results.dueToday', 'Due today')
                            : t('deadlineCalculator:results.daysRemaining', '{{days}} days remaining', { days: deadline.days_until_deadline })}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          ({deadline.days_from_trigger} {t('deadlineCalculator:results.daysFromTrigger', 'days from trigger')})
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                <CalendarDaysIcon className="h-16 w-16 mx-auto mb-4 text-gray-400 dark:text-gray-500" />
                <p>{t('deadlineCalculator:results.empty', 'Select deadline options and click Calculate to see your deadlines')}</p>
              </div>
            )}
          </div>
        </div>

        {/* Disclaimer */}
        <div className="mt-8 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
          <div className="flex items-start">
            <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 dark:text-yellow-400 mt-0.5 mr-3 flex-shrink-0" />
            <div className="text-sm text-yellow-800 dark:text-yellow-200">
              <p className="font-medium mb-1">{t('deadlineCalculator:disclaimer.title', 'Important Disclaimer')}</p>
              <p>
                {t('deadlineCalculator:disclaimer.text', 'These deadline calculations are provided for informational purposes only and should not be relied upon as legal advice. Actual deadlines may vary based on specific circumstances, local rules, court holidays, and other factors. Always consult with a qualified attorney to verify all legal deadlines.')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
